/**
 * 96-well plate layout, accession banding, and assign-test payload shape.
 * 384-well: extend later with different row count / labels.
 */

export const PLATE_96_ROW_COUNT = 8;
export const PLATE_96_COL_COUNT = 12;
export const PLATE_96_TOTAL_WELLS = PLATE_96_ROW_COUNT * PLATE_96_COL_COUNT;

export const PLATE_ROW_LETTERS_96 = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
] as const;

/** Column-major list: A1…H1, A2…H2, …, A12…H12 */
export function plate96WellLabels(): string[] {
  const labels: string[] = [];
  for (let col = 1; col <= PLATE_96_COL_COUNT; col++) {
    for (const letter of PLATE_ROW_LETTERS_96) {
      labels.push(`${letter}${col}`);
    }
  }
  return labels;
}

export const PLATE_96_WELL_LABELS = plate96WellLabels();
export const PLATE_96_WELL_COUNT = PLATE_96_WELL_LABELS.length;

/** Parsed well: rowIndex 0 = A, column 1–12 */
export function parseWellPosition96(
  wellPosition: string
): { rowIndex: number; column: number } | null {
  const m = /^([A-H])\s*(\d{1,2})$/i.exec(wellPosition.trim());
  if (!m) return null;
  const letter = m[1].toUpperCase();
  const col = parseInt(m[2], 10);
  if (col < 1 || col > PLATE_96_COL_COUNT) return null;
  const rowIndex = letter.charCodeAt(0) - "A".charCodeAt(0);
  if (rowIndex < 0 || rowIndex >= PLATE_96_ROW_COUNT) return null;
  return { rowIndex, column: col };
}

/**
 * Horizontal plate columns per accession band:
 * (totalWells / N) / 8 rows — e.g. 96/6/8 = 2.
 */
export function columnsPerAccession96(accessionsPerPlate: number): number {
  const n = Math.max(1, accessionsPerPlate);
  return PLATE_96_TOTAL_WELLS / n / PLATE_96_ROW_COUNT;
}

/** 0-based accession index for a well, or null if well invalid */
export function accessionIndexFromWell96(
  wellPosition: string,
  accessionsPerPlate: number
): number | null {
  const parsed = parseWellPosition96(wellPosition);
  if (!parsed) return null;
  const colsPer = columnsPerAccession96(accessionsPerPlate);
  return Math.floor((parsed.column - 1) / colsPer);
}

/** UI / DB placeholder: "Accession 1" … "Accession N" */
export function accessionPlaceholderLabel(accessionIndex0: number): string {
  return `Accession ${accessionIndex0 + 1}`;
}

export function getAccessionPlaceholderForWell96(
  wellPosition: string,
  accessionsPerPlate: number
): string {
  const idx = accessionIndexFromWell96(wellPosition, accessionsPerPlate);
  if (idx === null) return "—";
  if (idx < 0 || idx >= accessionsPerPlate) return "—";
  return accessionPlaceholderLabel(idx);
}

// ——— Assign test JSON (persisted shape; matches AssignTestJson NVARCHAR column) ———

/**
 * One saved entry — mirrors PlateSetupTestControlOption from the API so we
 * can round-trip without needing to re-fetch to reconstruct selected values.
 */
export type AssignTestEntry = {
  value: string;
  label: string;
  typeOfTest: string;
  isControl: boolean;
};

export type AssignTestJsonV1 = {
  version: 1;
  entries: AssignTestEntry[];
};

export function emptyAssignTestJson(): AssignTestJsonV1 {
  return { version: 1, entries: [] };
}

/** One row of TblIDLISPlateSetupDetail as the frontend will send / receive */
export type PlateSetupDetailRow = {
  wellPosition: string;
  /** Optional display key; same as wellPosition if unused */
  wellName: string | null;
  accessionPlaceholder: string;
  assignTestJson: AssignTestJsonV1;
};

export type PlateSlot = {
  plateSetupDetailId?: number;
  wellPosition: string;
  wellName?: string | null;
  wellRow?: string | null;
  wellColumn?: number | null;
  accessionPlaceholder?: string | null;
  accessionIndex?: number | null;
  assignTestJson: AssignTestJsonV1;
};

export type PlateSetupGeneratedDetail = {
  /** numeric position from backend (1–96) or well label string (A1…H12) */
  wellPosition?: string | number | null;
  wellName?: string | null;
  wellRow?: string | null;
  wellColumn?: number | null;
  accessionPlaceholder?: string | null;
  accessionIndex?: number | null;
  assignTests?: unknown[];
  /** backend wells shape */
  plateSetupDetailId?: number;
  plateSetupId?: number | null;
  assignTestJson?: string | null;
};

export function defaultSlots96(): PlateSlot[] {
  return PLATE_96_WELL_LABELS.map((wellPosition) => ({
    wellPosition,
    assignTestJson: emptyAssignTestJson(),
  }));
}

export function slotsFromGeneratedDetails(
  details: PlateSetupGeneratedDetail[] = []
): PlateSlot[] {
  return details
    .filter((d) => d.wellPosition !== null && d.wellPosition !== undefined)
    .map((detail) => {
      let assignTestJson: AssignTestJsonV1 = emptyAssignTestJson();
      if (detail.assignTestJson) {
        try {
          const parsed = JSON.parse(detail.assignTestJson);
          // Backend stores the entries array directly as the JSON string
          if (Array.isArray(parsed)) {
            assignTestJson = { version: 1, entries: parsed as AssignTestEntry[] };
          } else if (parsed?.entries) {
            assignTestJson = parsed as AssignTestJsonV1;
          }
        } catch {
          // leave as empty
        }
      }
      return {
        plateSetupDetailId: detail.plateSetupDetailId,
        wellPosition: String(detail.wellPosition),
        wellName: detail.wellName ?? String(detail.wellPosition),
        wellRow: detail.wellRow ?? null,
        wellColumn: detail.wellColumn ?? null,
        accessionPlaceholder: detail.accessionPlaceholder ?? null,
        accessionIndex: detail.accessionIndex ?? null,
        assignTestJson,
      };
    });
}

/** Build detail rows for save API (96-well only for now). */
export function buildPlateSetupDetailRows96(params: {
  accessionsPerPlate: number;
  slots: PlateSlot[];
}): PlateSetupDetailRow[] {
  return params.slots.map((slot) => ({
    wellPosition: slot.wellPosition,
    wellName: slot.wellName ?? slot.wellPosition,
    accessionPlaceholder:
      slot.accessionPlaceholder ??
      getAccessionPlaceholderForWell96(
        slot.wellPosition,
        params.accessionsPerPlate
      ),
    assignTestJson: slot.assignTestJson,
  }));
}

/**
 * Serialise the entries list to the NVARCHAR string the backend stores.
 * Format: "[{\"value\":\"...\",\"label\":\"...\",\"typeOfTest\":\"...\",\"isControl\":false}]"
 */
export function serializeAssignTestJsonForDb(json: AssignTestJsonV1): string {
  return JSON.stringify(json.entries);
}

// ——— Live lookup options (populated from GET api/plate-setup/test-control-lookups/{panelId}) ———

/**
 * A flat option that mirrors the API response exactly.
 * Used as the react-select option type for the Rack ID multi-select.
 */
export type PlateAssignOption = AssignTestEntry;

/**
 * Grouped option shape for react-select.
 * Tests and Controls are rendered as two separate groups so users can
 * immediately distinguish them without reading every label.
 */
export type PlateAssignGroup = {
  label: string;
  options: PlateAssignOption[];
};

/**
 * Convert a flat API list into two named groups: "Tests" and "Controls".
 * Controls are placed last so tests (the more common choice) appear first.
 */
export function groupTestControlOptions(
  options: PlateAssignOption[]
): PlateAssignGroup[] {
  const tests = options.filter((o) => !o.isControl);
  const controls = options.filter((o) => o.isControl);
  const groups: PlateAssignGroup[] = [];
  if (tests.length) groups.push({ label: "Tests", options: tests });
  if (controls.length) groups.push({ label: "Controls", options: controls });
  return groups;
}

/**
 * Reconstruct the currently-selected options from stored JSON.
 * Matches by value (id) so it works even if labels change on the server.
 */
export function selectValuesFromAssignJson(
  json: AssignTestJsonV1,
  allOptions: PlateAssignOption[] = []
): PlateAssignOption[] {
  if (!json?.entries?.length) return [];
  // Prefer a live match so the label stays fresh; fall back to the stored entry
  return json.entries.map(
    (entry) =>
      allOptions.find((o) => o.value === entry.value) ?? entry
  );
}

/**
 * Persist selected react-select options back into AssignTestJsonV1.
 * Each entry carries the full API shape so we never need the mock list again.
 */
export function assignJsonFromSelectOptions(
  selected: readonly PlateAssignOption[]
): AssignTestJsonV1 {
  return {
    version: 1,
    entries: selected.map((o) => ({
      value: o.value,
      label: o.label,
      typeOfTest: o.typeOfTest,
      isControl: o.isControl,
    })),
  };
}
